import { useEffect, useRef, useCallback } from 'react';
import { ChatHistoryMetaData, ChatItem } from '../types';

interface UseChatLogicProps {
  visible: boolean;
  campaign_id: number;
  user_id: number;
  receiver_id: number;
  getChatMessages: (campaign_id: number, user_id: number, receiver_id: number, page: number, pageSize: number) => void;
  chatHistory: ChatItem[];
  isChatHistoryLoading: boolean;
  chatHistoryMetaData?: ChatHistoryMetaData;
}

export const useChatLogic = ({
  visible,
  campaign_id,
  user_id,
  receiver_id,
  getChatMessages,
  chatHistory,
  isChatHistoryLoading,
  chatHistoryMetaData
}: UseChatLogicProps) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const loadMoreRef = useRef<HTMLDivElement>(null);
  const intersectionObserverRef = useRef<IntersectionObserver | null>(null);
  const previousChatLength = useRef<number>(0);

  // Load initial messages
  useEffect(() => {
    if (visible) {
      getChatMessages(campaign_id, user_id, receiver_id, 1, 10);
    }
  }, [visible, campaign_id, user_id, receiver_id, getChatMessages]);

  // Track chat history changes and maintain scroll position
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    // If new messages were loaded (length increased) and we're not at the bottom
    if (chatHistory.length > previousChatLength.current && previousChatLength.current > 0) {
      // Calculate how many new messages were added
      const newMessagesCount = chatHistory.length - previousChatLength.current;
      
      // Get all message elements to calculate their heights
      const messageElements = container.querySelectorAll('[data-message-index]');
      let additionalHeight = 0;
      
      // Sum up the heights of the new messages (which are at the beginning due to reverse order)
      for (let i = 0; i < Math.min(newMessagesCount, messageElements.length); i++) {
        const element = messageElements[i] as HTMLElement;
        additionalHeight += element.offsetHeight;
      }
      
      // Adjust scroll position to maintain visual position
      if (additionalHeight > 0) {
        container.scrollTop += additionalHeight;
      }
    }
    
    previousChatLength.current = chatHistory.length;
  }, [chatHistory.length]);

  const loadMore = useCallback(async () => {
    if (!chatHistoryMetaData?.has_next || isChatHistoryLoading) return;
    
    const nextPage = (chatHistoryMetaData.next || 1);
    await getChatMessages(campaign_id, user_id, receiver_id, nextPage, 10);
  }, [chatHistoryMetaData?.has_next, chatHistoryMetaData?.next, isChatHistoryLoading, getChatMessages, campaign_id, user_id, receiver_id]);

  // Setup Intersection Observer
  useEffect(() => {
    if (!loadMoreRef.current || !chatHistoryMetaData?.has_next) return;

    // Cleanup previous observer
    if (intersectionObserverRef.current) {
      intersectionObserverRef.current.disconnect();
    }

    // Create new Intersection Observer
    intersectionObserverRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !isChatHistoryLoading) {
            loadMore();
          }
        });
      },
      {
        root: containerRef.current,
        rootMargin: '50px 0px 0px 0px', // Trigger a bit before reaching the top
        threshold: 0.1,
      }
    );

    // Start observing the load more div
    if (loadMoreRef.current) {
      intersectionObserverRef.current.observe(loadMoreRef.current);
    }

    // Cleanup function
    return () => {
      if (intersectionObserverRef.current) {
        intersectionObserverRef.current.disconnect();
      }
    };
  }, [chatHistoryMetaData?.has_next, isChatHistoryLoading, loadMore]);

  return {
    containerRef,
    loadMoreRef,
    loadMore
  };
};
